// AppDrawer - side navigation for the Profile Manager dashboard.
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar, Box, Divider, Drawer, IconButton, List, ListItemButton, ListItemIcon,
  ListItemText, Tooltip, Typography,
} from '@mui/material';
import { User, Shield, CreditCard, HelpCircle, LogOut, ChevronLeft, ChevronRight } from 'lucide-react';

const AppDrawer = ({
  userName, userRole, onLogout, open, onClose, isMobile, collapsed, onCollapsedChange, avatarUrl,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const width = collapsed ? 65 : 260;

  const items = [
    { label: 'My Profile', path: '/profiles/profile', icon: <User size={20} /> },
    ...(['admin', 'superadmin'].includes(userRole) ? [{ label: 'Admin', path: '/profiles/admin', icon: <Shield size={20} /> }] : []),
    { label: 'Billing', path: '/profiles/billing', icon: <CreditCard size={20} /> },
    { label: 'Help', path: '/profiles/help', icon: <HelpCircle size={20} /> },
  ];

  const go = (path) => {
    navigate(path);
    if (isMobile) onClose?.();
  };

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, minHeight: 64 }}>
        <Avatar src={avatarUrl} sx={{ width: 36, height: 36 }}>{userName?.[0]?.toUpperCase()}</Avatar>
        {!collapsed && (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>{userName || 'User'}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap>{userRole}</Typography>
          </Box>
        )}
      </Box>
      <Divider />
      <List sx={{ flexGrow: 1 }}>
        {items.map((item) => (
          <Tooltip key={item.path} title={collapsed ? item.label : ''} placement="right">
            <ListItemButton selected={location.pathname.startsWith(item.path)} onClick={() => go(item.path)}>
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              {!collapsed && <ListItemText primary={item.label} />}
            </ListItemButton>
          </Tooltip>
        ))}
      </List>
      <Divider />
      <List>
        <ListItemButton onClick={onLogout}>
          <ListItemIcon sx={{ minWidth: 40 }}><LogOut size={20} /></ListItemIcon>
          {!collapsed && <ListItemText primary="Log out" />}
        </ListItemButton>
      </List>
      {!isMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pb: 1 }}>
          <IconButton size="small" onClick={() => onCollapsedChange?.(!collapsed)}>
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </IconButton>
        </Box>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onClose={onClose} sx={{ '& .MuiDrawer-paper': { width: 260 } }}>
        {content}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width,
        flexShrink: 0,
        '& .MuiDrawer-paper': { width, overflowX: 'hidden', transition: 'width 0.2s' },
      }}
    >
      {content}
    </Drawer>
  );
};

export default AppDrawer;
